import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import useHorizontalOverflow from "../hooks/useHorizontalOverflow";

const PLACEMENTS = ["hero", "section", "inline"];
const EVIDENCE_MODES = ["panel", "inline", "hidden"];
const EVIDENCE_CONTROL_PLACEMENTS = ["inline", "new-line"];
const TRANSITIONS = ["fade", "slide", "none"];

const NavigatorHeader = ({ eyebrow, title, intro }) => {
  if (!eyebrow && !title && !intro) return null;

  return (
    <header className="capability-navigator__header">
      {eyebrow && <p className="capability-navigator__eyebrow">{eyebrow}</p>}
      {title && <h2 className="capability-navigator__title">{title}</h2>}
      {intro && <p className="capability-navigator__intro">{intro}</p>}
    </header>
  );
};

const NavigatorControls = ({
  activeIndex,
  arrowBehavior,
  canScrollBack,
  canScrollForward,
  items,
  itemRefs,
  onItemKeyDown,
  onScroll,
  onScrollBack,
  onScrollForward,
  onSelect,
  navigationRef,
  scrollerRef,
  showArrows,
}) => (
  <div
    className={`capability-navigator__navigation${showArrows ? "" : " capability-navigator__navigation--without-arrows"}`}
    ref={navigationRef}
  >
    {showArrows && (
      <button
        aria-label={
          arrowBehavior === "scroll"
            ? "Scroll capability tabs left"
            : "Previous capability"
        }
        className="capability-navigator__arrow capability-navigator__arrow--previous"
        disabled={arrowBehavior === "scroll" && !canScrollBack}
        onClick={
          arrowBehavior === "scroll"
            ? onScrollBack
            : () => onSelect(activeIndex - 1)
        }
        type="button"
      />
    )}

    <div
      aria-label={arrowBehavior === "scroll" ? "Capability tabs" : undefined}
      className="capability-navigator__scroller"
      onScroll={arrowBehavior === "scroll" ? onScroll : undefined}
      ref={scrollerRef}
      tabIndex={arrowBehavior === "scroll" ? 0 : undefined}
    >
      <div
        className="capability-navigator__items"
        role="group"
        aria-label="Select a capability"
      >
        {items.map((item, index) => {
          const active = index === activeIndex;

          return (
            <button
              aria-pressed={active}
              className={`capability-navigator__item${active ? " capability-navigator__item--active" : ""}`}
              key={item.id}
              onClick={() => onSelect(index)}
              onKeyDown={(event) => onItemKeyDown(event, index)}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              tabIndex={active ? 0 : -1}
              type="button"
            >
              {item.label || item.title}
            </button>
          );
        })}
      </div>
    </div>

    {showArrows && (
      <button
        aria-label={
          arrowBehavior === "scroll"
            ? "Scroll capability tabs right"
            : "Next capability"
        }
        className="capability-navigator__arrow capability-navigator__arrow--next"
        disabled={arrowBehavior === "scroll" && !canScrollForward}
        onClick={
          arrowBehavior === "scroll"
            ? onScrollForward
            : () => onSelect(activeIndex + 1)
        }
        type="button"
      />
    )}
  </div>
);

const EvidenceList = ({ evidence }) => (
  <ul className="capability-navigator__evidence">
    {evidence.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

const DescriptionPanel = ({
  activeItem,
  evidenceId,
  evidenceLabel,
  evidenceMode,
  evidenceControlPlacement,
  expanded,
  onToggle,
  showDescription,
}) => {
  const evidence = activeItem.evidence || [];
  const hasEvidence = evidence.length > 0;
  const hasDescription = showDescription && activeItem.description;
  const panelEvidence = evidenceMode === "panel" && hasEvidence;
  const inlineEvidence = evidenceMode === "inline" && hasEvidence;

  if (!hasDescription && !panelEvidence && !inlineEvidence) return null;

  return (
    <div className="capability-navigator__body">
      <div
        className={`capability-navigator__selected-content capability-navigator__selected-content--evidence-${evidenceControlPlacement}`}
        key={activeItem.id}
      >
        {hasDescription && (
          <p className="capability-navigator__description">
            {activeItem.description}
          </p>
        )}

        {panelEvidence && (
          <button
            aria-controls={evidenceId}
            aria-expanded={expanded}
            className="capability-navigator__toggle"
            onClick={onToggle}
            type="button"
          >
            <span>{evidenceLabel}</span>
            <span className="capability-navigator__indicator" aria-hidden="true">
              {expanded ? "−" : "+"}
            </span>
          </button>
        )}
      </div>

      {panelEvidence && (
        <div id={evidenceId} hidden={!expanded}>
          {expanded && <EvidenceList evidence={evidence} />}
        </div>
      )}

      {inlineEvidence && (
        <div className="capability-navigator__inline-evidence">
          <EvidenceList evidence={evidence} />
        </div>
      )}
    </div>
  );
};

const CapabilityNavigator = ({
  eyebrow,
  title,
  intro,
  items = [],
  placement = "section",
  overlapHero = false,
  showArrows = true,
  showDescription = true,
  evidenceMode = "panel",
  evidenceLabel = "Highlights",
  evidenceControlPlacement = "inline",
  defaultActiveId,
  defaultExpanded = false,
  transition = "fade",
  arrowBehavior = "selection",
}) => {
  const instanceId = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const itemRefs = useRef([]);
  const navigationRef = useRef(null);
  const scrollerRef = useRef(null);
  const hasMounted = useRef(false);
  const [selectionHasOverflow, setSelectionHasOverflow] = useState(false);
  const resolvedPlacement = PLACEMENTS.includes(placement) ? placement : "section";
  const resolvedEvidenceMode = EVIDENCE_MODES.includes(evidenceMode)
    ? evidenceMode
    : "panel";
  const resolvedEvidenceControlPlacement =
    EVIDENCE_CONTROL_PLACEMENTS.includes(evidenceControlPlacement)
      ? evidenceControlPlacement
      : "inline";
  const resolvedTransition = TRANSITIONS.includes(transition) ? transition : "fade";
  const defaultIndex = Math.max(
    0,
    items.findIndex((item) => item.id === defaultActiveId),
  );
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const activeItem = items[activeIndex];
  const hasBody = Boolean(
    (showDescription && activeItem?.description) ||
      (resolvedEvidenceMode !== "hidden" && activeItem?.evidence?.length),
  );
  const usesAutomaticArrows = showArrows === "auto";
  const usesScrollArrows = arrowBehavior === "scroll";
  const scrollState = useHorizontalOverflow({
    containerRef: navigationRef,
    dependency: items,
    enabled: usesAutomaticArrows && usesScrollArrows,
    scrollerRef,
  });
  const hasOverflow = usesScrollArrows
    ? scrollState.hasOverflow
    : selectionHasOverflow;
  const resolvedShowArrows =
    showArrows === true || (usesAutomaticArrows && hasOverflow);

  useLayoutEffect(() => {
    if (!usesAutomaticArrows || usesScrollArrows) return undefined;

    const navigation = navigationRef.current;
    const scroller = scrollerRef.current;
    const itemContainer = scroller?.firstElementChild;
    if (!navigation || !itemContainer) return undefined;

    const measureOverflow = () => {
      const usesCompactNavigation = window.matchMedia(
        "(max-width: 768px)",
      ).matches;
      const navigationStyles = window.getComputedStyle(navigation);
      const itemStyles = window.getComputedStyle(itemContainer);
      const availableWidth =
        navigation.clientWidth -
        parseFloat(navigationStyles.paddingLeft) -
        parseFloat(navigationStyles.paddingRight);
      const gap = parseFloat(itemStyles.columnGap) || 0;
      const minimumItemsWidth = Array.from(itemContainer.children).reduce(
        (width, item) => {
          const basis = parseFloat(window.getComputedStyle(item).flexBasis);
          return width + (Number.isFinite(basis) ? basis : item.scrollWidth);
        },
        Math.max(0, itemContainer.children.length - 1) * gap,
      );
      const configuredMinimumWidth = parseFloat(itemStyles.minWidth) || 0;
      const nextHasOverflow =
        (!usesScrollArrows && usesCompactNavigation) ||
        Math.max(minimumItemsWidth, configuredMinimumWidth) >
          availableWidth + 1;

      setSelectionHasOverflow(nextHasOverflow);
    };

    measureOverflow();
    const resizeObserver = new ResizeObserver(measureOverflow);
    resizeObserver.observe(navigation);
    resizeObserver.observe(itemContainer);
    return () => {
      resizeObserver.disconnect();
    };
  }, [
    items,
    resolvedShowArrows,
    usesAutomaticArrows,
    usesScrollArrows,
  ]);

  useEffect(() => {
    if (!activeItem || !hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const item = itemRefs.current[activeIndex];
    const scroller = scrollerRef.current;

    if (!item || !scroller) return;

    scroller.scrollTo({
      left: item.offsetLeft - (scroller.clientWidth - item.offsetWidth) / 2,
      behavior:
        reduceMotion || resolvedTransition === "none" ? "auto" : "smooth",
    });
  }, [activeIndex, activeItem, resolvedTransition]);

  if (!items.length) return null;

  if (
    process.env.NODE_ENV !== "production" &&
    ![true, false, "auto"].includes(showArrows)
  ) {
    console.warn('CapabilityNavigator: showArrows must be true, false, or "auto".');
  }

  if (
    process.env.NODE_ENV !== "production" &&
    !EVIDENCE_CONTROL_PLACEMENTS.includes(evidenceControlPlacement)
  ) {
    console.warn(
      'CapabilityNavigator: evidenceControlPlacement must be "inline" or "new-line".',
    );
  }

  const selectItem = (index, moveFocus = false) => {
    const nextIndex = (index + items.length) % items.length;
    setActiveIndex(nextIndex);
    if (moveFocus) itemRefs.current[nextIndex]?.focus();
  };

  const scrollTabs = (direction) => {
    const scroller = scrollerRef.current;

    if (!scroller) return;

    scroller.scrollBy({
      left: direction * Math.max(scroller.clientWidth * 0.7, 240),
      behavior: "smooth",
    });
  };

  const handleItemKeyDown = (event, index) => {
    const keys = {
      ArrowRight: index + 1,
      ArrowDown: index + 1,
      ArrowLeft: index - 1,
      ArrowUp: index - 1,
      Home: 0,
      End: items.length - 1,
    };

    if (keys[event.key] === undefined) return;
    event.preventDefault();
    selectItem(keys[event.key], true);
  };

  const evidenceId = `capability-evidence-${instanceId}`;
  const classes = [
    "capability-navigator",
    `capability-navigator--placement-${resolvedPlacement}`,
    `capability-navigator--arrows-${
      arrowBehavior === "scroll" ? "scroll" : "selection"
    }`,
    overlapHero && "capability-navigator--overlap",
    `capability-navigator--transition-${resolvedTransition}`,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={classes}
      aria-label={title || eyebrow || "Capability navigator"}
    >
      <NavigatorHeader eyebrow={eyebrow} title={title} intro={intro} />

      <div
        className={`capability-navigator__surface${hasBody ? "" : " capability-navigator__surface--navigation-only"}`}
      >
        <NavigatorControls
          activeIndex={activeIndex}
          arrowBehavior={arrowBehavior}
          canScrollBack={scrollState.canScrollBack}
          canScrollForward={scrollState.canScrollForward}
          items={items}
          itemRefs={itemRefs}
          onItemKeyDown={handleItemKeyDown}
          onScroll={scrollState.updateOverflow}
          onScrollBack={() => scrollTabs(-1)}
          onScrollForward={() => scrollTabs(1)}
          onSelect={selectItem}
          navigationRef={navigationRef}
          scrollerRef={scrollerRef}
          showArrows={resolvedShowArrows}
        />

        {hasBody && (
          <span
            className="capability-navigator__page-boundary"
            aria-hidden="true"
          />
        )}

        <DescriptionPanel
          activeItem={activeItem}
          evidenceId={evidenceId}
          evidenceLabel={evidenceLabel}
          evidenceMode={resolvedEvidenceMode}
          evidenceControlPlacement={resolvedEvidenceControlPlacement}
          expanded={expanded}
          onToggle={() => setExpanded((current) => !current)}
          showDescription={showDescription}
        />
      </div>
    </section>
  );
};

export default CapabilityNavigator;
